import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const GardiennageSecurite = () => {
  return (
    <>
      <Helmet>
        <title>Télésurveillance & Gardiennage - ZiExter | Centre d'appels sécurité privée</title>
        <meta name="description" content="Centre d'appels dédié à la télésurveillance et au gardiennage. Gestion d'alarmes, dispatching d'interventions, support 24/7 pour sociétés de sécurité." />
        <link rel="canonical" href="https://ziexter.com/services/gardiennage-securite-privee" />
      </Helmet>

      <section className="page-hero bg-gradient-to-br from-secondary to-primary text-white">
        <div className="container-custom">
          <div className="max-w-3xl animate-fade-in-up">
            <nav className="flex items-center space-x-2 text-sm mb-4 text-gray-200">
              <Link to="/" className="hover:text-white">Accueil</Link>
              <span>/</span>
              <Link to="/services" className="hover:text-white">Services</Link>
              <span>/</span>
              <span className="text-white">Gardiennage & Sécurité</span>
            </nav>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Gardiennage et Sécurité Privée – ZiExter
            </h1>
            <p className="text-xl text-gray-100 mb-4">
              Votre centre d'appels dédié à la télésurveillance et au gardiennage
            </p>
            <p className="text-lg text-gray-200">
              Sociétés de sécurité privée, entreprises de télésurveillance, agences de gardiennage… ZiExter vous accompagne avec un centre d'appels spécialisé, disponible 24/7.
            </p>
          </div>
        </div>
      </section>

      <section className="py-8 bg-accent">
        <div className="container-custom">
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/devis" className="btn btn-secondary">Demandez un devis</Link>
            <Link to="/contact" className="btn btn-outline-white">Contactez nos experts sécurité</Link>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto animate-fade-in-up">
            <h2 className="section-title text-center mb-8">La sécurité ne dort jamais… votre centre d'appels non plus</h2>
            <p className="text-lg text-gray-700 mb-4">
              Alarmes déclenchées en pleine nuit, interventions urgentes, gestion de patrouilles, appels clients inquiets… Dans le secteur de la sécurité privée, la réactivité et la disponibilité sont vitales.
            </p>
            <p className="text-lg font-semibold text-primary">
              Avec ZiExter, externalisez votre centre d'appels sécurité et bénéficiez d'une équipe formée, disponible 24/7, et capable de gérer toutes vos opérations critiques.
            </p>
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Nos services pour la sécurité privée</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: 'Gestion des alarmes et alertes', desc: 'Réception d\'alertes intrusion, incendie, technique. Qualification immédiate et déclenchement de procédure.' },
              { title: 'Dispatching d\'interventions', desc: 'Coordination des équipes de gardiennage sur le terrain en temps réel.' },
              { title: 'Support clients 24/7', desc: 'Accueil téléphonique des clients protégés, assistance technique, gestion des demandes.' },
              { title: 'Levée de doute vidéo', desc: 'Vérification des alarmes par consultation des images de vidéosurveillance.' },
              { title: 'Suivi des rondes de sécurité', desc: 'Contrôle des pointages, alerte en cas d\'anomalie, reporting en temps réel.' },
              { title: 'Reporting et traçabilité', desc: 'Journalisation complète de toutes les interventions, alertes et appels.' }
            ].map((item, i) => (
              <div key={i} className="card hover-lift animate-fade-in-up" style={{ animationDelay: `${i * 50}ms` }}>
                <h3 className="font-bold mb-2">{item.title}</h3>
                <p className="text-sm text-gray-700">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Ils nous font confiance pour leur sécurité</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: 'Société de télésurveillance, Paris', problem: 'Difficulté à recruter des opérateurs de nuit.', solution: 'Externalisation de la permanence nocturne et week-ends.', result: 'Disponibilité 24/7 garantie, coûts réduits de 35%.' },
              { title: 'Agence de gardiennage, Lyon', problem: 'Coordination difficile des équipes sur le terrain.', solution: 'Centre de dispatching dédié avec suivi GPS.', result: 'Temps de réponse réduit de 40%, clients satisfaits.' },
              { title: 'Entreprise de sécurité privée, Belgique', problem: 'Pics d\'appels non gérés lors d\'événements.', solution: 'Équipe extensible selon les besoins.', result: 'Aucun appel perdu même en période de forte activité.' }
            ].map((item, i) => (
              <div key={i} className="card hover-lift animate-fade-in-up" style={{ animationDelay: `${i * 100}ms` }}>
                <h3 className="font-bold mb-3">{item.title}</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li><strong>Problème :</strong> {item.problem}</li>
                  <li><strong>Solution ZiExter :</strong> {item.solution}</li>
                  <li><strong>Résultat :</strong> {item.result}</li>
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Performances clés</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: '🚨', stat: '2 000', label: 'alarmes traitées chaque mois' },
              { icon: '⏱️', stat: '< 30 sec', label: 'Délai moyen de prise en charge' },
              { icon: '🌍', stat: '24/7', label: 'Disponibilité totale, 365j/an' },
              { icon: '⭐', stat: '99%', label: 'Fiabilité de la levée de doute' }
            ].map((item, i) => (
              <div key={i} className="card text-center hover-lift animate-fade-in-up" style={{ animationDelay: `${i * 50}ms` }}>
                <div className="text-3xl mb-2">{item.icon}</div>
                <div className="text-2xl font-bold text-primary mb-1">{item.stat}</div>
                <p className="text-sm text-gray-600">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Avis de professionnels de la sécurité</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { quote: 'ZiExter assure notre permanence nocturne avec un professionnalisme irréprochable. Une vraie tranquillité d\'esprit.', name: 'François Girard', role: 'Directeur, Société de télésurveillance' },
              { quote: 'Le dispatching de nos équipes est devenu fluide et efficace grâce à ZiExter. Nos clients sont ravis.', name: 'Marc Leblanc', role: 'Gérant, Agence de gardiennage' },
              { quote: 'Fiabilité, réactivité, reporting impeccable. ZiExter est notre partenaire de confiance depuis 3 ans.', name: 'Sophie Dumont', role: 'Responsable opérations, Sécurité privée' }
            ].map((item, i) => (
              <div key={i} className="card hover-lift animate-fade-in-up" style={{ animationDelay: `${i * 100}ms` }}>
                <p className="text-gray-700 mb-4 italic">"{item.quote}"</p>
                <div className="font-semibold">{item.name}</div>
                <div className="text-sm text-gray-600">{item.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">FAQ</h2>
          </div>
          <div className="max-w-4xl mx-auto space-y-4">
            {[
              { q: 'Vos opérateurs sont-ils formés à la sécurité privée ?', a: 'Oui, formation initiale sur les protocoles de sécurité + formation continue sur vos procédures.' },
              { q: 'Pouvez-vous gérer plusieurs sites simultanément ?', a: 'Absolument. Nos équipes gèrent des portefeuilles multi-sites sans difficulté.' },
              { q: 'Quelle est votre capacité en cas de pic d\'alertes ?', a: 'Nous dimensionnons nos équipes pour absorber les pics (événements, tempêtes, pannes).' },
              { q: 'Assurez-vous la conformité RGPD et sécurité des données ?', a: 'Oui, serveurs sécurisés, accès restreints, conformité RGPD totale.' },
              { q: 'Puis-je tester le service avant de m\'engager ?', a: 'Oui, période pilote possible pour valider notre efficacité opérationnelle.' }
            ].map((item, i) => (
              <details key={i} className="card cursor-pointer">
                <summary className="font-semibold p-4 flex justify-between items-center">
                  <span>Q : {item.q}</span>
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="p-4 pt-0 text-gray-600">{item.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gradient-to-r from-primary to-secondary text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center animate-fade-in-up">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              Protégez vos clients avec un centre d'appels d'élite
            </h2>
            <p className="text-xl text-gray-100 mb-8">
              ZiExter devient le cœur opérationnel de votre activité de sécurité privée. Réactivité maximale, disponibilité totale, professionnalisme garanti.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn btn-accent btn-lg">Contactez notre équipe sécurité</Link>
              <Link to="/devis" className="btn btn-outline-white btn-lg">Demandez un devis personnalisé</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default GardiennageSecurite;
